/* eslint-disable */
import { useEffect, useRef } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { setToken, setTokenBo } from "../utils/axios.create";
import { AUTH_CONSTANTS } from "../utils/auth";

/**
 * Hook to proactively refresh Auth0 tokens before they expire
 * This prevents failed API calls due to expired tokens
 *
 * @param enabled - Whether to enable proactive refresh (default: true)
 * @param intervalMs - Refresh interval in milliseconds (default: 5 minutes)
 */
export const useProactiveTokenRefresh = (
	enabled = true,
	intervalMs: number = AUTH_CONSTANTS.TOKEN_REFRESH_INTERVAL,
) => {
	const { getAccessTokenSilently, isAuthenticated } = useAuth0();
	const intervalRef = useRef<NodeJS.Timeout>();

	useEffect(() => {
		// Only run if authenticated and enabled
		if (!isAuthenticated || !enabled) {
			return;
		}

		const refreshToken = async () => {
			try {
				console.log("[TokenRefresh] Proactively refreshing token...");
				const token = await getAccessTokenSilently({
					cacheMode: "off", // Force fresh token
				});

				// Update axios instances
				setToken(token);
				setTokenBo(token);

				console.log("[TokenRefresh] Token refreshed successfully");
			} catch (error: any) {
				console.error("[TokenRefresh] Failed to refresh token:", error);

				// Don't throw error - let the 401 interceptor handle it on next API call
				// This prevents disrupting the user experience for transient errors
			}
		};

		// Set up periodic refresh.
		// The previous version also kicked off a refresh 1 second after the
		// hook mounted. On a fresh login the SDK already has a brand-new token
		// (good for ~24h), and on a returning session App.tsx has already
		// called getAccessTokenSilently({ cacheMode: "off" }) before this hook
		// gets enabled — so the immediate refresh was always paying an extra
		// Auth0 round-trip for nothing, in parallel with the post-login API
		// burst.
		intervalRef.current = setInterval(refreshToken, intervalMs);

		// Cleanup
		return () => {
			if (intervalRef.current) {
				clearInterval(intervalRef.current);
			}
		};
	}, [isAuthenticated, enabled, intervalMs, getAccessTokenSilently]);
};
